<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <TrustBar />
    <div class="pt-18"></div>
    <!-- Loading State -->
    <div v-if="loading" class="min-h-screen flex items-center justify-center">
      <div class="text-center">
        <div
          class="inline-block w-12 h-12 border-4 border-fuchsia-600 border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="mt-4 text-gray-600">Chargement...</p>
      </div>
    </div>

    <!-- Main Content -->
    <main v-else class="flex-1 py-8 md:py-12">
      <div class="max-w-7xl mx-auto px-4">
        <!-- Progress Steps -->
        <div class="flex items-center justify-center mb-8 max-w-md mx-auto">
          <div
            class="flex items-center gap-3"
            :class="{
              'text-fuchsia-800': currentStep >= 1,
              'text-gray-400': currentStep < 1
            }"
          >
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
              :class="
                currentStep >= 1 ? 'bg-fuchsia-800 text-white' : 'bg-gray-200'
              "
            >
              {{ currentStep > 1 ? '✓' : '1' }}
            </div>
            <span class="text-sm font-medium hidden sm:inline">Email</span>
          </div>
          <div
            class="flex-1 h-1 mx-2 sm:mx-4 rounded-full"
            :class="currentStep >= 2 ? 'bg-fuchsia-800' : 'bg-gray-200'"
          ></div>
          <div
            class="flex items-center gap-3"
            :class="{
              'text-fuchsia-800': currentStep >= 2,
              'text-gray-400': currentStep < 2
            }"
          >
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
              :class="
                currentStep >= 2 ? 'bg-fuchsia-800 text-white' : 'bg-gray-200'
              "
            >
              {{ currentStep > 2 ? '✓' : '2' }}
            </div>
            <span class="text-sm font-medium hidden sm:inline">Paiement</span>
          </div>
          <div
            class="flex-1 h-1 mx-2 sm:mx-4 rounded-full"
            :class="currentStep >= 3 ? 'bg-fuchsia-800' : 'bg-gray-200'"
          ></div>
          <div
            class="flex items-center gap-3"
            :class="{
              'text-fuchsia-800': currentStep >= 3,
              'text-gray-400': currentStep < 3
            }"
          >
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
              :class="
                currentStep >= 3 ? 'bg-fuchsia-800 text-white' : 'bg-gray-200'
              "
            >
              3
            </div>
            <span class="text-sm font-medium hidden sm:inline"
              >Confirmation</span
            >
          </div>
        </div>

        <!-- Two Column Layout -->
        <div class="flex flex-col grow md:grid md:grid-cols-2 h-full md:mt-6">
          <!-- Left Column: Form -->
          <section
            class="rounded-b-3xl items-center md:rounded-none py-0 order-1 md:order-2 mb-6"
          >
            <!-- Order Summary Card -->
            <div
              class="bg-gray-100 rounded-b-3xl md:rounded-3xl pt-4 pb-6 md:p-6 lg:p-16 w-full lg:w-[80%] px-6 mx-auto md:ml-auto md:mr-0"
            >
              <div class="space-y-4">
                <h3 class="font-bold text-xl max-md:hidden">
                  Résumé de la commande
                </h3>

                <!-- Product Info -->
                <div class="flex w-full max-w-[384px] overflow-hidden">
                  <div class="flex min-w-[104px] h-[65px] object-cover">
                    <img
                      :alt="productName"
                      class="object-cover rounded-md"
                      :src="productLogo"
                    />
                  </div>
                  <div
                    class="flex flex-col w-full max-w-full ml-4 justify-center"
                  >
                    <h4 class="font-bold text-lg text-gray-900">
                      {{ productName }}
                    </h4>
                    <span class="text-sm text-gray-900">{{ countryName }}</span>
                  </div>
                </div>

                <!-- Phone Number Display (Mobile Top-up only) -->
                <a
                  v-if="orderType === 'airtime' && phoneNumber"
                  class="w-full flex leading-5 ring-1 ring-gray-300 px-4 text-gray-900 rounded-2xl h-14 transition-colors items-center gap-2 relative font-bold bg-white cursor-pointer"
                  @click="editPhoneNumber"
                >
                  <div class="flex gap-2 min-w-4">
                    <span v-if="countryFlag">{{ countryFlag }}</span>
                    <img
                      v-else-if="countryLogo"
                      :src="countryLogo"
                      :alt="countryCode"
                      class="w-6 h-5 object-cover"
                    />
                    <div class="flex flex-col justify-center">
                      <span class="text-base">{{ phoneCode }}</span>
                    </div>
                  </div>
                  <div class="border-r-2 border-gray-300 h-6"></div>
                  <div class="flex flex-col justify-center">
                    <span>{{ formatPhoneNumber(phoneNumber) }}</span>
                  </div>
                  <svg
                    class="absolute right-4 w-6 h-6 text-fuchsia-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6C4.89543 5 4 5.89543 4 7V18C4 19.1046 4.89543 20 6 20H17C18.1046 20 19 19.1046 19 18V13M17.5858 3.58579C18.3668 2.80474 19.6332 2.80474 20.4142 3.58579C21.1953 4.36683 21.1953 5.63316 20.4142 6.41421L11.8284 15H9L9 12.1716L17.5858 3.58579Z"
                    />
                  </svg>
                </a>

                <!-- Price Breakdown -->
                <div class="space-y-2">
                  <div class="flex text-gray-700 justify-start">
                    <span class="text-sm"
                      >{{ quantity }}x {{ productName }}
                      {{ formatPrice(recipientUnitAmount) }} {{ recipientCurrencySymbol }}</span
                    >
                    <span class="grow text-right text-sm"
                      >{{ formatPrice(recipientTotal) }} {{ recipientCurrencySymbol }}</span
                    >
                  </div>
                  <div class="flex text-gray-700 justify-start">
                    <span class="text-sm">Frais de service</span>
                    <button
                      class="shrink-0 cursor-pointer justify-start -mb-1"
                      @click="showServiceFeesInfo"
                    >
                      <svg
                        class="w-4 h-4 ml-1 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                    <span class="grow text-right text-sm"
                      >{{ formatPrice(serviceFeeInRecipientCurrency) }} {{ recipientCurrencySymbol }}</span
                    >
                  </div>
                  <div class="flex text-gray-900 justify-start">
                    <span class="text-sm font-bold">Total</span>
                    <span class="grow text-right text-sm font-bold"
                      >{{ formatPrice(recipientTotal + serviceFeeInRecipientCurrency) }} {{ recipientCurrencySymbol }}</span
                    >
                  </div>
                </div>

                <!-- Coupon Code -->
                <form
                  @submit.prevent="applyCoupon"
                  class="flex flex-col w-full items-center"
                >
                  <button
                    type="button"
                    @click="couponExpanded = !couponExpanded"
                    class="flex items-center bg-white text-fuchsia-600 font-semibold max-w-fit h-8 px-4 py-2 rounded-full hover:bg-gray-50 transition"
                  >
                    <span class="text-base">Vous avez un code promo ?</span>
                    <svg
                      class="w-6 h-6 ml-3 transition-transform duration-300"
                      :class="{ 'rotate-180': couponExpanded }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                  <div v-if="couponExpanded" class="w-full mt-2">
                    <input
                      v-model="couponCode"
                      type="text"
                      placeholder="Entrez votre code"
                      class="w-full ring-1 ring-gray-300 rounded-2xl h-12 px-4 focus:ring-2 focus:ring-fuchsia-600 focus:outline-none"
                    />
                  </div>
                </form>
              </div>
            </div>
          </section>

          <!-- Right Column: Form -->
          <section
            class="flex flex-col grow order-2 md:order-1 px-6 md:px-[17.5%]"
          >
            <!-- Step 1: Email -->
            <div v-if="currentStep === 1" class="flex-1">
              <form @submit.prevent="goToPayment">
                <h1 class="font-bold text-xl mb-2 md:mb-4">
                  Saisissez votre adresse e-mail
                </h1>
                <div class="grid gap-2">
                  <label
                    class="w-full flex leading-5 ring-1 ring-gray-300 px-4 text-gray-900 rounded-2xl h-14 transition-colors"
                  >
                    <div class="flex flex-1 items-center">
                      <div
                        class="flex flex-col flex-1 relative transition-all mt-3"
                      >
                        <span
                          class="text-gray-500 select-none pointer-events-none absolute transition-all -top-4 text-sm"
                          >Adresse e-mail</span
                        >
                        <input
                          v-model="email"
                          type="email"
                          required
                          placeholder="test@recharge.com"
                          autocomplete="email"
                          inputmode="email"
                          class="bg-transparent focus:shadow-none text-base focus:border-0 focus:outline-none w-full mt-1 placeholder-transparent"
                        />
                      </div>
                    </div>
                  </label>
                  <p v-if="emailError" class="text-red-600 text-sm mt-1">
                    {{ emailError }}
                  </p>
                </div>
                <div class="mt-6">
                  <button
                    type="submit"
                    class="w-full h-12 rounded-full font-bold flex justify-center items-center bg-white border border-gray-300 hover:bg-gray-50 transition text-gray-900"
                  >
                    Continuer
                  </button>
                </div>
              </form>
            </div>

            <!-- Step 2: Payment -->
            <div v-if="currentStep === 2" class="flex-1">
              <h1 class="font-bold text-xl mb-2 md:mb-4">
                Paiement sécurisé
              </h1>
              <p class="text-gray-600 mb-4">
                Montant:
                <span class="font-bold text-fuchsia-800"
                  >{{ formatPrice(recipientTotal) }} {{ recipientCurrencySymbol }}</span
                >
              </p>

              <!-- Payment Provider Selection -->
              <div v-if="enabledProviders.length > 1" class="mb-6">
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Choisissez votre moyen de paiement</label
                >
                <div class="flex gap-3">
                  <button
                    v-for="provider in enabledProviders"
                    :key="provider.id"
                    type="button"
                    @click="selectProvider(provider.id)"
                    class="flex-1 py-3 px-4 rounded-xl border-2 font-medium transition-all flex items-center justify-center gap-2"
                    :class="
                      selectedProviderId === provider.id
                        ? 'border-fuchsia-600 bg-fuchsia-50 text-fuchsia-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    "
                  >
                    <span>{{ provider.name }}</span>
                  </button>
                </div>
              </div>

              <!-- Stripe Card Element -->
              <div v-show="selectedProviderId === 'stripe'">
                <form @submit.prevent="processPayment">
                  <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-2"
                      >Informations de carte</label
                    >

                    <!-- Loading indicator while Stripe initializes -->
                    <div
                      v-if="stripeInitializing"
                      class="flex flex-col items-center justify-center py-8"
                    >
                      <div
                        class="w-10 h-10 border-4 border-fuchsia-200 border-t-fuchsia-600 rounded-full animate-spin mb-3"
                      ></div>
                      <p class="text-gray-600 text-sm">
                        Chargement du système de paiement...
                      </p>
                    </div>

                    <div
                      id="card-element"
                      class="stripe-card-element"
                    ></div>
                    <div
                      id="card-errors"
                      class="text-red-600 text-sm mt-2"
                      role="alert"
                    ></div>
                  </div>

                  <div class="mt-6">
                    <button
                      type="submit"
                      :disabled="
                        processing || stripeInitializing || !cardComplete
                      "
                      class="w-full h-12 rounded-full font-bold flex justify-center items-center bg-white border border-gray-300 hover:bg-gray-50 transition text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {{
                        processing
                          ? 'Traitement...'
                          : 'Payer maintenant'
                      }}
                    </button>
                  </div>

                  <div class="mt-4 flex items-center justify-center gap-3">
                    <svg
                      class="w-6 h-6 text-gray-400"
                      viewBox="0 0 36 16"
                      fill="none"
                    >
                      <text
                        x="0"
                        y="12"
                        font-size="10"
                        font-weight="bold"
                        fill="#1A1F71"
                      >
                        VISA
                      </text>
                    </svg>
                    <svg
                      class="w-6 h-6 text-gray-400"
                      viewBox="0 0 36 16"
                      fill="none"
                    >
                      <circle cx="12" cy="8" r="6" fill="#EB001B" />
                      <circle cx="24" cy="8" r="6" fill="#F79E1B" />
                    </svg>
                  </div>
                </form>
              </div>

              <!-- PayPal Buttons -->
              <div v-show="selectedProviderId === 'paypal'">
                <!-- Loading indicator while PayPal initializes -->
                <div
                  v-if="stripeInitializing"
                  class="flex flex-col items-center justify-center py-8"
                >
                  <div
                    class="w-10 h-10 border-4 border-fuchsia-200 border-t-fuchsia-600 rounded-full animate-spin mb-3"
                  ></div>
                  <p class="text-gray-600 text-sm">
                    Chargement du système de paiement...
                  </p>
                </div>

                <!-- PayPal Button Container -->
                <div id="paypal-button-container" class="min-h-[50px]"></div>
              </div>

              <button
                type="button"
                @click="currentStep = 1"
                class="w-full py-3 mt-4 text-gray-600 hover:text-gray-900 font-medium"
              >
                Retour
              </button>
            </div>

            <!-- Step 3: Confirmation -->
            <div
              v-if="currentStep === 3"
              class="flex-1 flex flex-col items-center justify-center text-center py-12"
            >
              <div
                class="w-20 h-20 mb-6 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center shadow-xl"
              >
                <svg
                  class="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 class="text-3xl font-bold text-gray-900 mb-4">
                Commande confirmée !
              </h1>
              <p class="text-gray-600 mb-6">
                Vous recevrez votre code à l'adresse
                <strong>{{ email }}</strong>
              </p>
              <p class="text-sm text-gray-500 mb-4">
                Numéro de commande: #{{ orderId }}
              </p>
              <router-link
                to="/"
                class="inline-block py-4 px-8 bg-gradient-to-r from-fuchsia-800 to-fuchsia-900 text-white font-semibold rounded-xl hover:from-fuchsia-900 hover:to-purple-900 transition-all"
              >
                Retour à l'accueil
              </router-link>
            </div>

            <!-- Error Message -->
            <div
              v-if="error"
              class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mt-4"
            >
              {{ error }}
            </div>

            <!-- Footer -->
            <footer class="my-6">
              <div
                class="flex items-start gap-2 px-4 py-2 bg-gray-100 rounded-lg"
              >
                <svg
                  class="w-6 h-6 min-w-[24px] text-gray-600 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.7"
                    d="m1.366 11.57c0-6.3377 2.6571-9.0514 3.5451-9.9582h14.177c0.888 0.90514 3.5451 3.6206 3.5451 9.9582 0 7.2429-6.9823 9.9138-10.526 10.817-3.5469-0.9017-10.743-3.5726-10.743-10.815l0.00171-0.0018z"
                  />
                  <path
                    d="m12 12.429v3.4286"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="m12 12.429c1.6458 0 2.5715-0.9257 2.5715-2.5714s-0.9257-2.5714-2.5715-2.5714c-1.6457 0-2.5714 0.92571-2.5714 2.5714s0.92569 2.5714 2.5714 2.5714z"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span class="text-sm text-gray-700">
                  Nous assurons des paiements sécurisés. Vos données sont
                  cryptées à tout moment. Nous traiterons toutes les
                  informations que vous fournissez conformément à notre
                  Politique de confidentialité.
                </span>
              </div>
              <p class="text-center mt-4 text-gray-500 text-xs">
                © 2026 Recharge.com International B.V.
              </p>
            </footer>
          </section>
        </div>
      </div>
    </main>

    <!-- Custom Card Success Modal -->
    <Teleport to="body">
      <div
        v-if="showCustomCardModal"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @click.self="closeCustomCardModal"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

        <!-- Modal Content -->
        <div
          class="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        >
          <!-- Close Button -->
          <button
            @click="closeCustomCardModal"
            class="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
          >
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div class="p-8">
            <!-- Success Icon -->
            <div
              class="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center shadow-xl"
            >
              <svg
                class="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <!-- Title -->
            <h2 class="text-2xl font-bold text-gray-900 text-center mb-2">
              Commande confirmée !
            </h2>
            <p class="text-gray-600 text-center mb-6">
              Vos codes de recharge sont prêts
            </p>

            <!-- Order Info -->
            <div class="bg-gray-50 rounded-2xl p-4 mb-6">
              <div class="flex justify-between text-sm mb-2">
                <span class="text-gray-600">Quantité</span>
                <span class="font-medium text-gray-900"
                  >{{ customCardResult?.quantity }} code(s)</span
                >
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600">Stock restant</span>
                <span class="font-medium text-gray-900">{{
                  customCardResult?.remainingStock
                }}</span>
              </div>
            </div>

            <!-- Copy All Button -->
            <button
              @click="copyAllCodes"
              class="w-full mb-4 py-3 px-4 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white font-semibold rounded-xl hover:from-fuchsia-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              {{
                copiedCodeIndex === 'all' ? 'Copié !' : 'Copier tous les codes'
              }}
            </button>

            <!-- Codes List -->
            <div class="space-y-3 mb-6">
              <div
                v-for="(code, index) in customCardResult?.redeemCodes"
                :key="index"
                class="bg-gray-50 rounded-xl p-4 flex items-center justify-between gap-3"
              >
                <div class="flex-1 min-w-0">
                  <div class="text-xs text-gray-500 mb-1">
                    Code {{ index + 1 }}
                  </div>
                  <div class="font-mono font-bold text-gray-900 break-all">
                    {{ code }}
                  </div>
                </div>
                <button
                  @click="copyCode(code, index)"
                  class="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-white border-2 border-gray-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 transition-all"
                  :class="
                    copiedCodeIndex === index
                      ? 'border-emerald-500 bg-emerald-50'
                      : ''
                  "
                >
                  <svg
                    v-if="copiedCodeIndex === index"
                    class="w-5 h-5 text-emerald-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <svg
                    v-else
                    class="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Email Notice -->
            <div
              class="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"
            >
              <div class="flex gap-3">
                <svg
                  class="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
                <p class="text-sm text-amber-800">
                  Un email avec ces codes a également été envoyé à
                  <strong>{{ email }}</strong>
                </p>
              </div>
            </div>

            <!-- Done Button -->
            <button
              @click="closeCustomCardModal"
              class="w-full py-4 px-6 bg-gray-100 text-gray-900 font-semibold rounded-xl hover:bg-gray-200 transition-all"
            >
              Terminé
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
  getCurrentInstance,
  nextTick
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';
import { usePayment } from '@features/payment/composables/usePayment';
import { usePaymentState } from '@features/payment/composables/usePaymentState';

const route = useRoute();
const router = useRouter();
const { proxy } = getCurrentInstance();
const { $stores, $constants } = proxy;

const currentStep = ref(1);
const email = ref(route.query?.email);
const product = ref(null);
const recipientUnitAmount = ref(0); // Recipient unit amount from query
const quantity = ref(parseInt(route.query?.quantity) || 1);
const recipientTotal = ref(0); // Recipient total (unit * quantity)
const recipientCurrency = ref(null); // Recipient currency from product
const localAmount = ref(0); // Local amount for payment (converted)
const emailError = ref(null);
const orderId = ref('');
const couponCode = ref('');
const couponExpanded = ref(false);
const phoneNumber = ref(route.query?.phone || '');

// Stripe-specific refs
const stripeInitializing = ref(false);
const cardComplete = ref(false);
const cardholderName = ref('');

// PayPal-specific refs
const paypalButtonsRendered = ref(false);

// Track which providers have been initialized (to avoid re-initializing)
const stripeInitialized = ref(false);
const paypalInitialized = ref(false);

// Custom card success modal state
const showCustomCardModal = ref(false);
const customCardResult = ref(null);
const copiedCodeIndex = ref(null);

// Payment composables - will be initialized after totalAmount/currency are defined
const paymentComposable = ref(null);
const paymentStateComposable = ref(null);

// Computed properties for template - delegated to payment composables when ready
const enabledProviders = computed(() => paymentComposable.value?.enabledProviders || []);
// Local ref to store the selected provider ID
const selectedProviderId = ref(null);
const selectedProvider = computed(() => paymentComposable.value?.selectedProvider || null);
const processing = computed(() => paymentComposable.value?.processing?.value || false);
const paymentError = computed(() => paymentComposable.value?.error?.value || null);

// Function to select a payment provider
const selectProvider = (providerId) => {
  selectedProviderId.value = providerId;
  paymentComposable.value?.selectProvider(providerId);
};

const isProcessing = computed(() => paymentStateComposable.value?.isProcessing?.value || false);
const isSucceeded = computed(() => paymentStateComposable.value?.isSucceeded?.value || false);

// Helper functions
const setSucceeded = (data) => paymentStateComposable.value?.setSucceeded?.(data);
const setFailed = (err) => paymentStateComposable.value?.setFailed?.(err);
const reset = () => paymentStateComposable.value?.reset?.();

// Error computed from payment composables
const error = computed(() => paymentError.value);

// Remove old Stripe-specific variables (replaced by composables)
let stripeClientSecret = null;

// New refs for API-driven data
const loading = ref(true);
const operators = ref([]);
const operator = ref(null);
const country = ref(null);
const products = ref([]);
const esimPackages = ref([]);
const esimPackage = ref(null);
const customCard = ref(null);

const currency = computed(() => {
  // For custom cards, use the card's own currency
  if (orderType.value === 'custom_card' && customCard.value?.currency) {
    return customCard.value.currency;
  }
  // For gift cards, use the product's currency (recipientCurrency)
  if (orderType.value === 'gift_card' && recipientCurrency.value) {
    return recipientCurrency.value;
  }
  // For eSIM, use the package currency if set
  if (orderType.value === 'esim' && recipientCurrency.value) {
    return recipientCurrency.value;
  }
  if (country.value) {
    return country.value.currency || 'FCFA';
  }
  const code = route.query.countryCode || 'BJ';
  return ['BJ', 'TG', 'CI', 'SN', 'ML', 'NE', 'BF'].includes(code)
    ? 'FCFA'
    : 'XOF';
});

const currencySymbol = computed(() => {
  return currency.value === 'FCFA' || currency.value === 'XOF'
    ? ''
    : currency.value;
});

const recipientCurrencySymbol = computed(() => {
  if (!recipientCurrency.value) return currencySymbol.value;
  const symbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'XOF': 'FCFA',
    'XAF': 'FCFA'
  };
  return symbols[recipientCurrency.value] || recipientCurrency.value;
});

const orderType = computed(() => {
  return route.query.orderType || 'gift_card';
});

// Product display values - using fetched operator data
const productName = computed(() => {
  if (orderType.value === 'airtime') {
    return (
      operator.value?.name ||
      operator.value?.operatorName ||
      route.query.operatorName ||
      'Mobile'
    );
  }
  if (orderType.value === 'esim') {
    return esimPackage.value?.name || route.query.packageName || 'eSIM';
  }
  if (orderType.value === 'custom_card') {
    return (
      customCard.value?.title ||
      product.value?.productName ||
      route.query.productName ||
      'Carte personnalisée'
    );
  }
  return product.value?.productName || route.query.productName || 'Produit';
});

const productLogo = computed(() => {
  if (orderType.value === 'airtime') {
    const logos = operator.value?.logoUrls;
    if (logos && logos.length > 0) {
      return logos[1] || logos[0];
    }
  }
  if (orderType.value === 'custom_card') {
    // For custom cards, use the imageUrl from customCard
    if (customCard.value?.imageUrl) {
      return customCard.value.imageUrl;
    }
    // Fall back to product logoUrls if customCard image is not available
    const logos = product.value?.logoUrls;
    if (logos && logos.length > 0) {
      return logos[1] || logos[0];
    }
  }
  if (orderType.value === 'gift_card') {
    const logos = product.value?.logoUrls;
    if (logos && logos.length > 0) {
      return logos[1] || logos[0];
    }
  }
  if (orderType.value === 'esim') {
    // Return a placeholder or gradient for eSIM
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTA0IiBoZWlnaHQ9IjY1IiB2aWV3Qm94PSIwIDAgMTA0IDY1IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDQiIGhlaWdodD0iNjUiIGZpbGw9InVybCgjZ3JhZGllbnQpIi8+PHRleHQgeD0iNTIiIHk9IjQwIiBmaWxsPSJ3aGl0ZSIgZm9udC1zaXplPSIyNCIgZm9udC13ZWlnaHQ9ImJvbGQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPlnTaU08L3RleHQ+PGRlZnM+PGxpbmVhckdyYWRpZW50IGlkPSJncmFkaWVudCIgeDE9IjAlIiB5MT0iMCUiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+PHN0b3Agb2Zmc2V0PSIwJSIgc3RvcC1jb2xvcj0iIzEwZTk4NCIvPjxzdG9wIG9mZnNldD0iMTAwJSIgc3RvcC1jb2xvcj0iIzE0YjhhMCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjwvc3ZnPg==';
  }
  return (
    product.value?.logoUrls?.[0] ||
    route.query.logoUrl ||
    '/images/placeholder.png'
  );
});

const countryName = computed(() => {
  return (
    country.value?.countryName ||
    $stores.global.state.selectedCountry?.countryName ||
    'Bénin'
  );
});

const countryCode = computed(() => {
  return country.value?.countryCode || route.query.countryCode || 'BJ';
});

const countryFlag = computed(() => {
  const code = countryCode.value;
  const flags = {
    BJ: '🇧🇯',
    TG: '🇹🇬',
    CI: '🇨🇮',
    SN: '🇸🇳',
    ML: '🇲🇱',
    FR: '🇫🇷',
    BE: '🇧🇪',
    CH: '🇨🇭',
    CM: '🇨🇲',
    CD: '🇨🇩',
    NE: '🇳🇪',
    BF: '🇧🇫',
    GN: '🇬🇳'
  };
  return flags[code] || '';
});

const countryLogo = computed(() => {
  return (
    country.value?.localLogoUrl ||
    $stores.global.state.selectedCountry?.localLogoUrl
  );
});

const phoneCode = computed(() => {
  return (
    country.value?.phonePrefix ||
    `+${country.value?.callingCode}` ||
    $stores.global.state.selectedCountry?.phonePrefix ||
    '+229'
  );
});

const serviceFee = computed(() => {
  // Service fee is 2% of the local amount
  return Math.round(localAmount.value * 0.02);
});

// Service fee in recipient currency for display
const serviceFeeInRecipientCurrency = computed(() => {
  // For custom cards, no conversion
  if (orderType.value === 'custom_card') {
    return serviceFee.value;
  }
  // For airtime, use operator's exchange rate
  if (orderType.value === 'airtime' && operator.value) {
    const exchangeRate = operator.value?.recipientCurrencyToSenderCurrencyExchangeRate ||
                        operator.value?.senderCurrencyToRecipientCurrencyExchangeRate ||
                        operator.value?.fxRate ||
                        600;
    // If same currency, no conversion
    if (operator.value?.destinationCurrencyCode === country.value?.currency) {
      return serviceFee.value;
    }
    return Math.round(serviceFee.value / exchangeRate);
  }
  // For gift cards, no conversion needed if already in same currency
  if (product.value) {
    // If gift card currency matches local currency, no conversion
    if (recipientCurrency.value && currency.value && recipientCurrency.value === currency.value) {
      return serviceFee.value;
    }
    // Otherwise use product's exchange rate
    const exchangeRate = product.value?.recipientCurrencyToSenderCurrencyExchangeRate || 600;
    return Math.round(serviceFee.value / exchangeRate);
  }
  // Fallback
  return serviceFee.value;
});

const totalAmount = computed(() => {
  return localAmount.value + serviceFee.value;
});

// Payment amounts prepared for Stripe and PayPal
// These use the local currency when supported, otherwise convert
const stripePaymentAmount = computed(() => {
  // Stripe supports XOF natively (since 2024), use local currency
  const currencyCode = currency.value?.toUpperCase() || 'XOF';
  // XOF and similar currencies don't have decimals
  const hasDecimals = !['XOF', 'XAF', 'CDF', 'BIF', 'DJF'].includes(currencyCode);
  return hasDecimals ? Math.round(totalAmount.value * 100) : Math.round(totalAmount.value);
});

const stripePaymentCurrency = computed(() => {
  return currency.value?.toLowerCase() || 'xof';
});

const paypalPaymentAmount = computed(() => {
  // PayPal doesn't support XOF/FCFA, need conversion
  const currencyCode = currency.value?.toUpperCase() || 'XOF';
  const supportedCurrencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NOK', 'SEK', 'DKK', 'PLN', 'HUF', 'CZK', 'ILS', 'MXN', 'BRL', 'PHP'];

  if (supportedCurrencies.includes(currencyCode)) {
    // Currency supported, return amount in cents for consistency
    // Note: XOF and similar currencies don't have decimals
    const hasDecimals = !['XOF', 'XAF', 'CDF', 'BIF', 'DJF'].includes(currencyCode);
    return hasDecimals ? Math.round(totalAmount.value * 100) : Math.round(totalAmount.value);
  }

  // XOF/FCFA not supported, convert to EUR (preferred for Africa) or USD
  const rate = currencyCode === 'XOF' || currencyCode === 'FCFA' ? 0.0015 : 0.0016; // XOF to EUR rate
  return Math.round(totalAmount.value * rate * 100); // Return in cents
});

const paypalPaymentCurrency = computed(() => {
  const currencyCode = currency.value?.toUpperCase() || 'XOF';
  const supportedCurrencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NOK', 'SEK', 'DKK', 'PLN', 'HUF', 'CZK', 'ILS', 'MXN', 'BRL', 'PHP'];

  if (supportedCurrencies.includes(currencyCode)) {
    return currencyCode.toLowerCase();
  }

  // Default to EUR for XOF/FCFA (linked via fixed exchange rate)
  return 'eur';
});

// Fetch operators from API (similar to MobileTopUpProductPage)
const fetchOperators = async (countryCode) => {
  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      let timeoutId;
      let handled = false;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        bus.off('products.fetch_operators_success', successHandler);
        bus.off('products.fetch_operators_error', errorHandler);
      };

      const successHandler = (data) => {
        if (handled) return;
        handled = true;
        cleanup();
        operators.value = data.data?.operatorList || data.data || data || [];
        resolve();
      };

      const errorHandler = (err) => {
        if (handled) return;
        handled = true;
        cleanup();
        reject(err);
      };

      // Set timeout for 10 seconds
      timeoutId = setTimeout(() => {
        if (handled) return;
        handled = true;
        cleanup();
        reject(new Error('Timeout fetching operators'));
      }, 10000);

      bus.on('products.fetch_operators_success', successHandler);
      bus.on('products.fetch_operators_error', errorHandler);

      bus.emit('products.fetch_operators', {
        countryCode: countryCode,
        type: 'airtime'
      });
    });
  } catch (e) {
    console.error('Error fetching operators:', e);
  }
};

// Fetch products (gift cards) from API
const fetchProducts = async (countryCode) => {
  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      let timeoutId;
      let handled = false;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        bus.off('cards.fetch_by_country_success', successHandler);
        bus.off('cards.fetch_by_country_error', errorHandler);
      };

      const successHandler = (data) => {
        if (handled) return;
        handled = true;
        cleanup();
        products.value = data.data || data || [];
        resolve();
      };

      const errorHandler = (err) => {
        if (handled) return;
        handled = true;
        cleanup();
        reject(err);
      };

      // Set timeout for 10 seconds
      timeoutId = setTimeout(() => {
        if (handled) return;
        handled = true;
        cleanup();
        reject(new Error('Timeout fetching products'));
      }, 10000);

      bus.on('cards.fetch_by_country_success', successHandler);
      bus.on('cards.fetch_by_country_error', errorHandler);

      bus.emit('cards.fetch_by_country', countryCode);
    });
  } catch (e) {
    console.error('Error fetching products:', e);
  }
};

// Fetch eSIM packages from API
const fetchEsimPackages = async (locationCode) => {
  try {
    const constants = await import('@core/utils/constants');
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      let timeoutId;
      let handled = false;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
      };

      const successHandler = (data) => {
        if (handled) return;
        handled = true;
        cleanup();
        esimPackages.value = data || [];
        resolve();
      };

      const errorHandler = (err) => {
        if (handled) return;
        handled = true;
        cleanup();
        reject(err);
      };

      // Set timeout for 10 seconds
      timeoutId = setTimeout(() => {
        if (handled) return;
        handled = true;
        cleanup();
        reject(new Error('Timeout fetching eSIM packages'));
      }, 10000);

      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);

      bus.emit(constants.EVENTS.ESIM.FETCH_PACKAGES, locationCode);
    });
  } catch (e) {
    console.error('Error fetching eSIM packages:', e);
  }
};

// Fetch custom card from local API
const fetchCustomCard = async (cardId) => {
  try {
    const response = await fetch(
      `${$constants.ENDPOINTS.LOCAL_API}/custom-cards/${cardId}`
    );
    const data = await response.json();

    if (response.ok) {
      customCard.value = data.data || data;
    } else {
      throw new Error(data.message || 'Failed to fetch custom card');
    }
  } catch (e) {
    console.error('Error fetching custom card:', e);
    throw e;
  }
};

// Map country code to esim location code
const getEsimLocationCode = (countryCode) => {
  const mapping = {
    BJ: 'BJ',
    TG: 'TG',
    CI: 'CI',
    SN: 'SN',
    ML: 'ML',
    FR: 'FR',
    BE: 'BE',
    CH: 'CH',
    CM: 'CM',
    CD: 'CD',
    NE: 'NE',
    BF: 'BF',
    GN: 'GN'
  };
  return mapping[countryCode] || countryCode;
};

onMounted(async () => {
  try {
    recipientUnitAmount.value = parseFloat(route.query.amount) || 0;

    // For custom card orders, fetch from local API (check FIRST)
    if (orderType.value === 'custom_card' && route.query.productId) {
      const cardId = route.query.productId;
      const countryCode = route.query.countryCode || 'BJ';

      // Find country from library
      const foundCountry = Countries.find(countryCode);
      if (foundCountry) {
        country.value = foundCountry;
        // Update global store
        $stores.global.selectCountry(countryCode);
      }

      // Fetch custom card details
      await fetchCustomCard(cardId);

      // Use custom card as product
      if (customCard.value) {
        product.value = {
          productName: customCard.value.title,
          logoUrls: customCard.value.imageUrl
            ? [customCard.value.imageUrl]
            : [],
          recipientCurrency: customCard.value.currency || 'XOF'
        };
        // Set amounts from custom card price
        localAmount.value = customCard.value.price * quantity.value;
        recipientTotal.value = localAmount.value;
        recipientCurrency.value = customCard.value.currency || 'XOF';
        recipientUnitAmount.value = customCard.value.price;
      }
    }
    // For airtime orders, fetch operator from API
    else if (
      orderType.value === 'airtime' &&
      route.query.operatorId &&
      route.query.countryCode
    ) {
      const operatorId = parseInt(route.query.operatorId);
      const countryCode = route.query.countryCode;

      // Find country from library
      const foundCountry = Countries.find(countryCode);
      if (foundCountry) {
        country.value = foundCountry;
        // Update global store
        $stores.global.selectCountry(countryCode);
      }

      // Fetch operators from API
      await fetchOperators(countryCode);

      // Find the specific operator
      const foundOperator = operators.value.find(
        (op) => op.operatorId === operatorId
      );
      if (foundOperator) {
        operator.value = foundOperator;

        // Set recipient currency from operator's destination currency
        recipientCurrency.value = foundOperator.destinationCurrencyCode || country.value?.currency || 'XOF';

        // Calculate amounts
        // For airtime, recipientUnitAmount from query is the local amount (sender amount in XOF)
        // We need to convert it to recipient currency for display
        const exchangeRate = foundOperator.recipientCurrencyToSenderCurrencyExchangeRate ||
                            foundOperator.senderCurrencyToRecipientCurrencyExchangeRate ||
                            foundOperator.fxRate ||
                            600; // Default XOF to EUR rate

        // If destination currency is different from local currency, convert
        if (foundOperator.destinationCurrencyCode && foundOperator.destinationCurrencyCode !== country.value?.currency) {
          // recipientUnitAmount.value is the local amount (e.g., XOF)
          // Convert to recipient currency for display (e.g., EUR)
          const unitAmountInRecipientCurrency = Math.round(recipientUnitAmount.value / exchangeRate);
          recipientTotal.value = unitAmountInRecipientCurrency * quantity.value;
          // Update recipientUnitAmount to be in recipient currency for display
          recipientUnitAmount.value = unitAmountInRecipientCurrency;
          localAmount.value = (route.query.amount ? parseFloat(route.query.amount) : unitAmountInRecipientCurrency * exchangeRate);
        } else {
          // Same currency - no conversion needed
          recipientTotal.value = recipientUnitAmount.value * quantity.value;
          localAmount.value = recipientTotal.value;
        }
      }
    }
    // For gift card orders, fetch product from API
    else if (
      orderType.value === 'gift_card' &&
      route.query.productId &&
      route.query.countryCode
    ) {
      const productId = parseInt(route.query.productId);
      const countryCode = route.query.countryCode;

      // Find country from library
      const foundCountry = Countries.find(countryCode);
      if (foundCountry) {
        country.value = foundCountry;
        // Update global store
        $stores.global.selectCountry(countryCode);
      }

      // Fetch products from API
      await fetchProducts(countryCode);

      // Find the specific product
      const foundProduct = products.value.find(
        (p) => p.productId === productId
      );
      if (foundProduct) {
        product.value = foundProduct;

        // For gift cards, use the product's currency directly (no conversion needed for payment)
        recipientTotal.value = recipientUnitAmount.value * quantity.value;
        recipientCurrency.value = foundProduct.recipientCurrencyCode || 'USD';
        // localAmount should be in the same currency as the product (for payment)
        localAmount.value = recipientTotal.value;
      }
    }
    // For eSIM orders, fetch package from API
    else if (
      orderType.value === 'esim' &&
      route.query.packageCode &&
      route.query.countryCode
    ) {
      const packageCode = route.query.packageCode;
      const countryCode = route.query.countryCode;

      // Find country from library
      const foundCountry = Countries.find(countryCode);
      if (foundCountry) {
        country.value = foundCountry;
        // Update global store
        $stores.global.selectCountry(countryCode);
      }

      // Fetch eSIM packages from API
      const locationCode = getEsimLocationCode(countryCode);
      await fetchEsimPackages(locationCode);

      // Find the specific package
      const foundPackage = esimPackages.value.find(
        (pkg) => pkg.packageCode === packageCode
      );
      if (foundPackage) {
        esimPackage.value = foundPackage;

        // Calculate amounts
        // eSIM packages have price in local currency (API returns in cents)
        // Convert from cents to units if needed
        const packagePrice = foundPackage.price || 0;
        const unitPrice = packagePrice / 100; // API returns price in cents

        // Set amounts
        recipientUnitAmount.value = unitPrice;
        recipientTotal.value = unitPrice * quantity.value;
        localAmount.value = recipientTotal.value;

        // Set currency (eSIM prices are typically in local currency)
        recipientCurrency.value = country.value?.currency || 'XOF';
      }
    }

    loading.value = false;

    // Now initialize payment composables after totalAmount and currency are defined
    paymentComposable.value = usePayment(totalAmount, currency);
    paymentStateComposable.value = usePaymentState();
  } catch (e) {
    console.error('Error in onMounted:', e);
    error.value = 'Erreur lors du chargement';
  }
});

// Initialize the selected payment provider
const initializePaymentProvider = async () => {
  stripeInitializing.value = true;

  try {
    // Get the composable
    const composable = paymentComposable.value;
    if (!composable) {
      throw new Error('Payment composable not initialized. Please try again.');
    }

    // Initialize the provider (loads SDK)
    await composable.initializeProvider();

    const provider = selectedProvider.value;
    if (!provider) {
      throw new Error('No payment provider selected');
    }

    if (provider.id === 'stripe') {
      // Create PaymentIntent and mount card element for Stripe
      const paymentData = await composable.createPayment(
        stripePaymentAmount.value, // Amount in cents (or units for XOF)
        stripePaymentCurrency.value, // Use local currency (XOF supported)
        { email: email.value }
      );

      stripeClientSecret = paymentData.clientSecret;

      await nextTick();
      await nextTick();

      // Mount the card element
      const cardEl = provider.mountCardElement(
        'card-element',
        stripeClientSecret,
        {
          variables: {
            colorPrimary: '#9333ea',
            colorBackground: '#ffffff',
            colorText: '#1a1a1a'
          }
        }
      );

      // Listen for card validation changes
      cardEl.on('change', (event) => {
        cardComplete.value = event.complete;
        const errorDiv = document.getElementById('card-errors');
        if (errorDiv) {
          if (event.error) {
            errorDiv.textContent = event.error.message;
            errorDiv.style.display = 'block';
          } else {
            errorDiv.style.display = 'none';
          }
        }
      });

      // Wait for element to be ready
      cardEl.on('ready', () => {
        stripeInitializing.value = false;
        stripeInitialized.value = true;
      });

      // Fallback timeout
      setTimeout(() => {
        if (stripeInitializing.value) {
          stripeInitializing.value = false;
          stripeInitialized.value = true;
        }
      }, 2000);
    } else if (provider.id === 'paypal') {
      // Render PayPal buttons
      await nextTick();
      await nextTick();

      provider.renderButtons('paypal-button-container', {
        amount: paypalPaymentAmount.value, // Already converted if needed
        currency: paypalPaymentCurrency.value, // Uses local currency if supported, else EUR/USD
        metadata: {
          email: email.value,
          orderId: `ORD-${Date.now()}`
        },
        onSuccess: async (data) => {
          setSucceeded(data);
          await finalizeOrder();
        },
        onError: (err) => {
          setFailed(err);
        },
        onCancel: (data) => {
          reset();
        }
      });

      paypalButtonsRendered.value = true;
      paypalInitialized.value = true;
      stripeInitializing.value = false;
    }
  } catch (e) {
    console.error('Error initializing payment provider:', e);
    error.value = 'Impossible de charger le système de paiement: ' + e.message;
    stripeInitializing.value = false;
  }
};

// Watch for provider changes and initialize only if not already done
watch(selectedProviderId, async (newProviderId, oldProviderId) => {
  // Only initialize if we're in payment step, provider actually changed, and not yet initialized
  if (currentStep.value === 2 && newProviderId && newProviderId !== oldProviderId) {
    // Check if this specific provider has been initialized
    const alreadyInitialized = newProviderId === 'stripe'
      ? stripeInitialized.value
      : newProviderId === 'paypal'
      ? paypalInitialized.value
      : false;

    if (!alreadyInitialized) {
      console.log(`Provider ${newProviderId} not yet initialized, initializing...`);
      await initializePaymentProvider();
    } else {
      console.log(`Provider ${newProviderId} already initialized, skipping re-initialization`);
    }
  }
});

onUnmounted(() => {
  // Cleanup handled by payment composables
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price);
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 8) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`;
  }
  return phone;
};

const editPhoneNumber = () => {
  router.back();
};

const showServiceFeesInfo = () => {
  alert(
    'Les frais de service couvrent les coûts de traitement et de livraison de votre recharge.'
  );
};

const applyCoupon = () => {
  // TODO: Implement coupon code validation
  alert('Code promo non valide');
};

const goToPayment = async () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.value || !emailRegex.test(email.value)) {
    emailError.value = 'Veuillez entrer une adresse e-mail valide';
    return;
  }

  emailError.value = null;
  error.value = null;

  // Just move to payment step - provider will be initialized when user selects it
  currentStep.value = 2;
};

const processPayment = async () => {
  // Only process Stripe payments (PayPal handles itself via callbacks)
  const provider = selectedProvider.value;
  if (!provider || provider.id !== 'stripe') {
    return;
  }

  if (!cardComplete.value) {
    error.value = 'Veuillez compléter les informations de carte';
    return;
  }

  processing.value = true;
  error.value = null;

  try {
    // Get the Stripe provider's card element
    const cardElement = provider.cardElement;

    if (!cardElement) {
      throw new Error('Card element not initialized');
    }

    // Get the composable's processPayment method
    const composable = paymentComposable.value;
    if (!composable?.processPayment) {
      throw new Error('Payment composable not available');
    }

    // Process payment via Stripe
    await composable.processPayment({
      clientSecret: stripeClientSecret,
      cardElement: cardElement,
      cardHolder: cardholderName.value || email.value.split('@')[0]
    });

    setSucceeded({ provider: 'stripe' });
    await finalizeOrder();
  } catch (e) {
    console.error('Payment error:', e);
    setFailed(e);
    error.value = 'Erreur lors du paiement: ' + e.message;
  } finally {
    processing.value = false;
  }
};

const finalizeOrder = async () => {
  try {
    orderId.value = 'ORD-' + Date.now();

    if (orderType.value === 'gift_card') {
      await buyGiftCard();
    } else if (orderType.value === 'airtime') {
      await buyAirtime();
    } else if (orderType.value === 'esim') {
      await buyEsim();
    } else if (orderType.value === 'custom_card') {
      await buyCustomCard();
    }

    currentStep.value = 3;
  } catch (e) {
    console.error('Error finalizing order:', e);
    error.value = 'Erreur lors de la finalisation de la commande';
  }
};

const buyGiftCard = async () => {
  const { bus } = await import('@core/events/eventSystem');

  // Get quantity from route query, default to 1
  const giftQuantity = parseInt(route.query?.quantity) || 1;
  // Calculate unit price from total amount (excluding service fees - use localAmount)
  const unitPrice = Math.round(localAmount.value / giftQuantity);

  return new Promise((resolve, reject) => {
    const requestId = 'gift-' + Date.now();

    const successHandler = (result) => {
      bus.off('cards.buy_success', successHandler);
      bus.off('cards.buy_error', errorHandler);
      resolve(result);
    };

    const errorHandler = (err) => {
      bus.off('cards.buy_success', successHandler);
      bus.off('cards.buy_error', errorHandler);
      reject(err);
    };

    bus.on('cards.buy_success', successHandler);
    bus.on('cards.buy_error', errorHandler);

    bus.emit('cards.buy', {
      service: 'ecoxurf',
      requestId,
      currency: 'XOF',
      quantity: giftQuantity,
      unitPrice: unitPrice,
      id: requestId,
      recipientEmail: email.value,
      senderName: email.value.split('@')[0],
      productId: product.value?.productId,
      countryCode: route.query.countryCode || 'BJ'
    });
  });
};

const buyAirtime = async () => {
  const { bus } = await import('@core/events/eventSystem');

  return new Promise((resolve, reject) => {
    const requestId = 'airtime-' + Date.now();

    const successHandler = (result) => {
      bus.off('products.buy_mobile_success', successHandler);
      bus.off('products.buy_mobile_error', errorHandler);
      resolve(result);
    };

    const errorHandler = (err) => {
      bus.off('products.buy_mobile_success', successHandler);
      bus.off('products.buy_mobile_error', errorHandler);
      reject(err);
    };

    bus.on('products.buy_mobile_success', successHandler);
    bus.on('products.buy_mobile_error', errorHandler);

    bus.emit('products.buy_mobile', {
      service: 'ecoxurf',
      requestId,
      operationName: route.query.operationName || 'airtime',
      amount: localAmount.value,
      id:
        operator.value?.operatorId ||
        product.value?.operatorId ||
        route.query.operatorId ||
        '1',
      recipientPhone: phoneNumber.value || route.query.phone || '22901020304',
      senderPhone: phoneNumber.value || route.query.phone || '22901020304',
      operatorId:
        operator.value?.operatorId ||
        product.value?.operatorId ||
        parseInt(route.query.operatorId || '1'),
      countryCode: country.value?.countryCode || route.query.countryCode || 'BJ'
    });
  });
};

const buyEsim = async () => {
  const constants = await import('@core/utils/constants');
  console.log('🚀 ~ buyEsim ~ constants:', constants);
  const { bus } = await import('@core/events/eventSystem');

  // Use fetched package data
  const packageCode = esimPackage.value?.packageCode || route.query.packageCode;
  // Calculate single price from total and quantity
  const singlePrice = Math.round(localAmount.value / quantity.value);

  // Create multiple eSIM orders based on quantity
  const promises = [];
  for (let i = 0; i < quantity.value; i++) {
    promises.push(
      new Promise((resolve, reject) => {
        const transactionId = `esim-${Date.now()}-${i}`;

        const successHandler = (result) => {
          bus.off(constants.EVENTS.ESIM.CREATE_ORDER_SUCCESS, successHandler);
          bus.off(constants.EVENTS.ESIM.CREATE_ORDER_ERROR, errorHandler);
          resolve(result);
        };

        const errorHandler = (err) => {
          bus.off(constants.EVENTS.ESIM.CREATE_ORDER_SUCCESS, successHandler);
          bus.off(constants.EVENTS.ESIM.CREATE_ORDER_ERROR, errorHandler);
          reject(err);
        };

        bus.on(constants.EVENTS.ESIM.CREATE_ORDER_SUCCESS, successHandler);
        bus.on(constants.EVENTS.ESIM.CREATE_ORDER_ERROR, errorHandler);

        bus.emit(constants.EVENTS.ESIM.CREATE_ORDER, {
          transactionId,
          packageCode,
          amount: singlePrice,
          email: email.value
        });
      })
    );
  }

  // Wait for all orders to complete
  return Promise.all(promises);
};

const buyCustomCard = async () => {
  const productId = route.query?.productId;

  if (!productId) {
    throw new Error('Product ID is required for custom card purchase');
  }

  const response = await fetch(
    `${$constants.ENDPOINTS.LOCAL_API}/custom-cards/${productId}/purchase`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value,
        quantity: quantity.value || 1
      })
    }
  );

  const result = await response.json();

  if (result.success) {
    // Store result for modal display
    customCardResult.value = result;
    // Show success modal
    showCustomCardModal.value = true;
    // Still move to confirmation step
    return result;
  } else {
    throw new Error(result.error || 'Failed to purchase custom card');
  }
};

// Copy code to clipboard
const copyCode = async (code, index) => {
  try {
    await navigator.clipboard.writeText(code);
    copiedCodeIndex.value = index;
    setTimeout(() => {
      copiedCodeIndex.value = null;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy code:', err);
  }
};

// Copy all codes at once
const copyAllCodes = async () => {
  if (!customCardResult.value?.redeemCodes) return;

  const allCodes = customCardResult.value.redeemCodes.join('\n');
  try {
    await navigator.clipboard.writeText(allCodes);
    copiedCodeIndex.value = 'all';
    setTimeout(() => {
      copiedCodeIndex.value = null;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy codes:', err);
  }
};

// Close modal and go to home
const closeCustomCardModal = () => {
  showCustomCardModal.value = false;
  router.push('/');
};
</script>

<style scoped>
.stripe-card-element {
  padding: 14px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: white;
  transition: all 0.2s ease;
}

.stripe-card-element:focus-within {
  border-color: #9333ea;
  box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
}

.stripe-card-element :deep iframe {
  background: transparent;
}

input:focus {
  outline: none;
}
</style>
